import type {
  AnchorGateStatusResponse,
  AuthConfigResponse,
  LoginRequest,
  RegisterRequest,
  StudioIllustrationsRequest,
  StudioInputsRequest,
  StudioQueuedResponse,
  StudioState,
  AssetCandidateListResponse,
  AssetDetail,
  AutosaveDraftRequest,
  AutosaveDraftResponse,
  BookVersionDetail,
  BookVersionListResponse,
  BookVersionPageStatusResponse,
  BranchBookVersionRequest,
  BranchBookVersionResponse,
  CharacterAnchorDetail,
  CharacterAnchorListResponse,
  CharacterProfileDetail,
  CharacterProfileListResponse,
  ConfigurationListResponse,
  ConfigurationSummary,
  ConfigurationVersionDetail,
  ConfigurationVersionListResponse,
  ConfirmUploadRequest,
  ConfirmUploadResponse,
  CreateConfigurationRequest,
  CreateDownloadGrantRequest,
  CreateSessionRequest,
  DownloadGrantDetail,
  DownloadGrantListResponse,
  GenerateAnchorPackRequest,
  GenerateStoryPlanRequest,
  GenerationRunDetail,
  GenerationRunListResponse,
  HealthResponse,
  JobDetail,
  JobListResponse,
  PageParams,
  PresignDownloadResponse,
  PresignUploadRequest,
  PresignUploadResponse,
  PrincipalResponse,
  Problem,
  RenderedAssetListResponse,
  ScenePlanDetail,
  SessionResponse,
  StartConfigurationRequest,
  StartConfigurationResponse,
  StoryPlanDetail,
  StoryPlanListResponse,
  StoryPlanWithScenesDetail,
  TemplateListResponse,
  TemplateSummary,
  TemplateVersionDetail,
  TemplateVersionListResponse,
  UpdateConfigurationRequest,
  UpdateScenePlanRequest,
} from "./types";

const TOKEN_STORAGE_KEY = "zauber.access_token";

export class ApiError extends Error {
  readonly problem: Problem;
  readonly status: number;

  constructor(problem: Problem) {
    super(problem.title || `API error ${problem.status}`);
    this.name = "ApiError";
    this.problem = problem;
    this.status = problem.status;
  }
}

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    // ignore storage errors (e.g. private mode)
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  // Skip auth header (e.g. for login). Defaults to false.
  noAuth?: boolean;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(
    path,
    typeof window !== "undefined" ? window.location.origin : "http://localhost",
  );
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  // Return path + search so the Vite proxy / same-origin is used.
  return url.pathname + url.search;
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, query, signal, noAuth } = options;
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (!noAuth) {
    const token = getStoredToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (!response.ok) {
    if (contentType.includes("application/problem+json")) {
      const problem = (await response.json()) as Problem;
      throw new ApiError(problem);
    }
    throw new ApiError({
      type: "about:blank",
      title: response.statusText || `HTTP ${response.status}`,
      status: response.status,
    });
  }

  if (contentType.includes("application/json")) {
    return (await response.json()) as T;
  }
  return undefined as T;
}

// --- Health ---
export const healthApi = {
  check: (signal?: AbortSignal) =>
    request<HealthResponse>("/health", { signal, noAuth: true }),
};

// --- Auth ---
export const authApi = {
  createSession: (payload: CreateSessionRequest) =>
    request<SessionResponse>("/api/v1/auth/session", {
      method: "POST",
      body: payload,
      noAuth: true,
    }),
  me: (signal?: AbortSignal) =>
    request<PrincipalResponse>("/api/v1/auth/me", { signal }),
  config: (signal?: AbortSignal) =>
    request<AuthConfigResponse>("/api/v1/auth/config", {
      signal,
      noAuth: true,
    }),
  register: (payload: RegisterRequest) =>
    request<SessionResponse>("/api/v1/auth/register", {
      method: "POST",
      body: payload,
      noAuth: true,
    }),
  login: (payload: LoginRequest) =>
    request<SessionResponse>("/api/v1/auth/login", {
      method: "POST",
      body: payload,
      noAuth: true,
    }),
  logout: () => request<void>("/api/v1/auth/logout", { method: "POST" }),
};

// --- Studio (guided book creation) ---
const studioPath = (configurationId: string) =>
  `/api/v1/configurations/${encodeURIComponent(configurationId)}/studio`;

export const studioApi = {
  get: (configurationId: string, signal?: AbortSignal) =>
    request<StudioState>(studioPath(configurationId), { signal }),
  prepareCharacter: (configurationId: string, payload?: StudioInputsRequest) =>
    request<StudioQueuedResponse>(`${studioPath(configurationId)}/character`, {
      method: "POST",
      body: payload,
    }),
  regenerateAnchor: (configurationId: string, anchorType: string) =>
    request<StudioQueuedResponse>(
      `${studioPath(configurationId)}/anchors/${encodeURIComponent(anchorType)}/regenerate`,
      { method: "POST" },
    ),
  selectAnchor: (configurationId: string, anchorId: string) =>
    request<StudioQueuedResponse>(
      `${studioPath(configurationId)}/anchors/${encodeURIComponent(anchorId)}/select`,
      { method: "POST" },
    ),
  approveCharacter: (configurationId: string) =>
    request<StudioQueuedResponse>(
      `${studioPath(configurationId)}/character/approve`,
      { method: "POST" },
    ),
  generateStory: (configurationId: string, payload?: StudioInputsRequest) =>
    request<StudioQueuedResponse>(`${studioPath(configurationId)}/story`, {
      method: "POST",
      body: payload,
    }),
  updatePage: (configurationId: string, pageNumber: number, text: string) =>
    request<StudioQueuedResponse>(
      `${studioPath(configurationId)}/pages/${pageNumber}`,
      { method: "PATCH", body: { text } },
    ),
  generateIllustrations: (
    configurationId: string,
    payload: StudioIllustrationsRequest,
  ) =>
    request<StudioQueuedResponse>(
      `${studioPath(configurationId)}/illustrations`,
      { method: "POST", body: payload },
    ),
  selectCandidate: (
    configurationId: string,
    pageNumber: number,
    candidateId: string,
  ) =>
    request<StudioQueuedResponse>(
      `${studioPath(configurationId)}/pages/${pageNumber}/candidates/${encodeURIComponent(candidateId)}/select`,
      { method: "POST" },
    ),
  assembleBook: (configurationId: string) =>
    request<StudioQueuedResponse>(`${studioPath(configurationId)}/book`, {
      method: "POST",
    }),
};

// --- Templates ---
export const templatesApi = {
  list: (params: PageParams & { category?: string; language?: string } = {}) =>
    request<TemplateListResponse>("/api/v1/templates", {
      query: {
        page: params.page,
        page_size: params.page_size,
        category: params.category,
        language: params.language,
      },
    }),
  get: (templateId: string) =>
    request<TemplateSummary>(
      `/api/v1/templates/${encodeURIComponent(templateId)}`,
    ),
  listVersions: (templateId: string, params: PageParams = {}) =>
    request<TemplateVersionListResponse>(
      `/api/v1/templates/${encodeURIComponent(templateId)}/versions`,
      { query: { page: params.page, page_size: params.page_size } },
    ),
  getVersion: (templateId: string, version: number) =>
    request<TemplateVersionDetail>(
      `/api/v1/templates/${encodeURIComponent(templateId)}/versions/${version}`,
    ),
};

// --- Configurations ---
export const configurationsApi = {
  list: (params: PageParams & { status?: string } = {}) =>
    request<ConfigurationListResponse>("/api/v1/configurations", {
      query: {
        page: params.page,
        page_size: params.page_size,
        status: params.status,
      },
    }),
  get: (configurationId: string) =>
    request<ConfigurationSummary>(
      `/api/v1/configurations/${encodeURIComponent(configurationId)}`,
    ),
  create: (payload: CreateConfigurationRequest) =>
    request<ConfigurationSummary>("/api/v1/configurations", {
      method: "POST",
      body: payload,
    }),
  update: (configurationId: string, payload: UpdateConfigurationRequest) =>
    request<ConfigurationSummary>(
      `/api/v1/configurations/${encodeURIComponent(configurationId)}`,
      { method: "PUT", body: payload },
    ),
  archive: (configurationId: string) =>
    request<void>(
      `/api/v1/configurations/${encodeURIComponent(configurationId)}`,
      {
        method: "DELETE",
      },
    ),
  listVersions: (configurationId: string, params: PageParams = {}) =>
    request<ConfigurationVersionListResponse>(
      `/api/v1/configurations/${encodeURIComponent(configurationId)}/versions`,
      { query: { page: params.page, page_size: params.page_size } },
    ),
  getVersion: (configurationId: string, version: number) =>
    request<ConfigurationVersionDetail>(
      `/api/v1/configurations/${encodeURIComponent(configurationId)}/versions/${version}`,
    ),
  start: (payload: StartConfigurationRequest) =>
    request<StartConfigurationResponse>("/api/v1/configurations:start", {
      method: "POST",
      body: payload,
    }),
  getLatestDraft: (configurationId: string) =>
    request<ConfigurationVersionDetail>(
      `/api/v1/configurations/${encodeURIComponent(configurationId)}/draft`,
    ),
  autosaveDraft: (configurationId: string, payload: AutosaveDraftRequest) =>
    request<AutosaveDraftResponse>(
      `/api/v1/configurations/${encodeURIComponent(configurationId)}/draft:autosave`,
      { method: "PUT", body: payload },
    ),
};

// --- Uploads ---
export const uploadsApi = {
  presign: (payload: PresignUploadRequest) =>
    request<PresignUploadResponse>("/api/v1/uploads/presign", {
      method: "POST",
      body: payload,
    }),
  confirm: (payload: ConfirmUploadRequest) =>
    request<ConfirmUploadResponse>("/api/v1/uploads/confirm", {
      method: "POST",
      body: payload,
    }),
};

// --- Characters ---
export const charactersApi = {
  listProfiles: (configurationId: string) =>
    request<CharacterProfileListResponse>(
      `/api/v1/configurations/${encodeURIComponent(configurationId)}/character-profiles`,
    ),
  getProfile: (characterProfileId: string) =>
    request<CharacterProfileDetail>(
      `/api/v1/character-profiles/${encodeURIComponent(characterProfileId)}`,
    ),
  listAnchors: (characterProfileId: string) =>
    request<CharacterAnchorListResponse>(
      `/api/v1/character-profiles/${encodeURIComponent(characterProfileId)}/anchors`,
    ),
  updateAnchor: (
    characterAnchorId: string,
    payload: { selected?: boolean; qa_status?: string },
  ) =>
    request<CharacterAnchorDetail>(
      `/api/v1/character-anchors/${encodeURIComponent(characterAnchorId)}`,
      { method: "PATCH", body: payload },
    ),
  approveProfile: (characterProfileId: string) =>
    request<CharacterProfileDetail>(
      `/api/v1/character-profiles/${encodeURIComponent(characterProfileId)}/approve`,
      { method: "POST", body: {} },
    ),
  getAnchorGateStatus: (characterProfileId: string) =>
    request<AnchorGateStatusResponse>(
      `/api/v1/character-profiles/${encodeURIComponent(characterProfileId)}/anchor-gate`,
    ),
  generateAnchorPack: (
    characterProfileId: string,
    payload: GenerateAnchorPackRequest = {},
  ) =>
    request<CharacterAnchorListResponse>(
      `/api/v1/character-profiles/${encodeURIComponent(characterProfileId)}/anchor-pack`,
      { method: "POST", body: payload },
    ),
};

// --- Story Plans ---
export const storyPlansApi = {
  list: (configurationId: string) =>
    request<StoryPlanListResponse>(
      `/api/v1/configurations/${encodeURIComponent(configurationId)}/story-plans`,
    ),
  get: (storyPlanId: string) =>
    request<StoryPlanDetail>(
      `/api/v1/story-plans/${encodeURIComponent(storyPlanId)}`,
    ),
  getFull: (storyPlanId: string) =>
    request<StoryPlanWithScenesDetail>(
      `/api/v1/story-plans/${encodeURIComponent(storyPlanId)}/full`,
    ),
  getScene: (storyPlanId: string, pageNumber: number) =>
    request<ScenePlanDetail>(
      `/api/v1/story-plans/${encodeURIComponent(storyPlanId)}/scenes/${pageNumber}`,
    ),
  generate: (configurationId: string, payload: GenerateStoryPlanRequest) =>
    request<StoryPlanDetail>(
      `/api/v1/configurations/${encodeURIComponent(configurationId)}/story-plans/generate`,
      { method: "POST", body: payload },
    ),
  updateScene: (
    storyPlanId: string,
    pageNumber: number,
    payload: UpdateScenePlanRequest,
  ) =>
    request<ScenePlanDetail>(
      `/api/v1/story-plans/${encodeURIComponent(storyPlanId)}/scenes/${pageNumber}`,
      { method: "PATCH", body: payload },
    ),
};

// --- Generation Runs ---
export const generationRunsApi = {
  list: (
    configurationId: string,
    params: { status?: string; run_type?: string } = {},
  ) =>
    request<GenerationRunListResponse>(
      `/api/v1/configurations/${encodeURIComponent(configurationId)}/generation-runs`,
      { query: { status: params.status, run_type: params.run_type } },
    ),
  get: (generationRunId: string) =>
    request<GenerationRunDetail>(
      `/api/v1/generation-runs/${encodeURIComponent(generationRunId)}`,
    ),
  listCandidates: (generationRunId: string) =>
    request<AssetCandidateListResponse>(
      `/api/v1/generation-runs/${encodeURIComponent(generationRunId)}/candidates`,
    ),
  selectCandidate: (generationRunId: string, candidateId: string) =>
    request<void>(
      `/api/v1/generation-runs/${encodeURIComponent(generationRunId)}/candidates/${encodeURIComponent(candidateId)}/select`,
      { method: "POST" },
    ),
};

// --- Assets ---
export const assetsApi = {
  get: (assetId: string) =>
    request<AssetDetail>(`/api/v1/assets/${encodeURIComponent(assetId)}`),
  delete: (assetId: string) =>
    request<void>(`/api/v1/assets/${encodeURIComponent(assetId)}`, {
      method: "DELETE",
    }),
  presignDownload: (assetId: string) =>
    request<PresignDownloadResponse>(
      `/api/v1/assets/${encodeURIComponent(assetId)}/download`,
    ),
};

// --- Book Versions ---
export const bookVersionsApi = {
  list: (configurationId: string) =>
    request<BookVersionListResponse>(
      `/api/v1/configurations/${encodeURIComponent(configurationId)}/book-versions`,
    ),
  get: (bookVersionId: string) =>
    request<BookVersionDetail>(
      `/api/v1/book-versions/${encodeURIComponent(bookVersionId)}`,
    ),
  approve: (bookVersionId: string) =>
    request<BookVersionDetail>(
      `/api/v1/book-versions/${encodeURIComponent(bookVersionId)}/approve`,
      { method: "POST" },
    ),
  getPages: (bookVersionId: string) =>
    request<BookVersionPageStatusResponse>(
      `/api/v1/book-versions/${encodeURIComponent(bookVersionId)}/pages`,
    ),
  branch: (bookVersionId: string, payload: BranchBookVersionRequest = {}) =>
    request<BranchBookVersionResponse>(
      `/api/v1/book-versions/${encodeURIComponent(bookVersionId)}/branch`,
      { method: "POST", body: payload },
    ),
  listRenderedAssets: (bookVersionId: string) =>
    request<RenderedAssetListResponse>(
      `/api/v1/book-versions/${encodeURIComponent(bookVersionId)}/rendered-assets`,
    ),
  presignRenderedDownload: (bookVersionId: string, renderedAssetId: string) =>
    request<PresignDownloadResponse>(
      `/api/v1/book-versions/${encodeURIComponent(bookVersionId)}/rendered-assets/${encodeURIComponent(renderedAssetId)}/download`,
    ),
  createDownloadGrant: (
    bookVersionId: string,
    renderedAssetId: string,
    payload: CreateDownloadGrantRequest,
  ) =>
    request<DownloadGrantDetail>(
      `/api/v1/book-versions/${encodeURIComponent(bookVersionId)}/rendered-assets/${encodeURIComponent(renderedAssetId)}/download-grants`,
      { method: "POST", body: payload },
    ),
  listDownloadGrants: (bookVersionId: string, renderedAssetId: string) =>
    request<DownloadGrantListResponse>(
      `/api/v1/book-versions/${encodeURIComponent(bookVersionId)}/rendered-assets/${encodeURIComponent(renderedAssetId)}/download-grants`,
    ),
};

// --- Jobs ---
export const jobsApi = {
  list: (params: PageParams & { status?: string; job_type?: string } = {}) =>
    request<JobListResponse>("/api/v1/jobs", {
      query: {
        page: params.page,
        page_size: params.page_size,
        status: params.status,
        job_type: params.job_type,
      },
    }),
  get: (jobId: string) =>
    request<JobDetail>(`/api/v1/jobs/${encodeURIComponent(jobId)}`),
  listForConfiguration: (configurationId: string) =>
    request<JobListResponse>(
      `/api/v1/configurations/${encodeURIComponent(configurationId)}/jobs`,
    ),
};
